import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Camera, Sparkles, Image, Users, BarChart3, ArrowRight, Check } from "lucide-react";
import Logo from "@/components/Logo";
import NavArrows from "@/components/NavArrows";
import { ConsentModal, hasSignedConsent } from "@/components/ConsentModal";

// Inline Google G glyph — avoids adding a new icon dependency and renders
// crisply at any button size. Official multi-color per Google's brand guide.
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [consentOpen, setConsentOpen] = React.useState(false);

  const doRedirect = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignIn = () => {
    // Explicit consent gate — only skipped if the user has already accepted on
    // this device (localStorage `pb.consent.v1`). The backend still stamps
    // `terms_accepted_at` when the account is created, giving us an audit
    // trail regardless of the localStorage state.
    if (hasSignedConsent()) return doRedirect();
    setConsentOpen(true);
  };

  const features = [
    { icon: Sparkles, title: "AI-Powered Culling", desc: "Let AI classify, score and cull thousands of frames in minutes — powered by GPT vision." },
    { icon: Image, title: "Non-destructive Editor", desc: "Fine-tune exposure, color, clarity and crops without ever touching your originals." },
    { icon: Users, title: "Client Galleries", desc: "Tokenized, password-protected galleries with selections, revisions and paid downloads." },
    { icon: BarChart3, title: "Studio Analytics", desc: "Track revenue, gallery engagement and tax-ready summaries — all in one place." },
  ];

  return (
    <div className="min-h-screen">
      <header className="glass fixed top-0 inset-x-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <Logo size={32} withWordmark />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-stone-400">
            <NavArrows />
            <Link to="/showcase" className="hover:text-stone-100" data-testid="nav-showcase">Showcase</Link>
            <Link to="/discover" className="hover:text-stone-100" data-testid="nav-discover">Discover</Link>
            <Link to="/pricing" className="hover:text-stone-100" data-testid="nav-pricing">Pricing</Link>
          </nav>
          {user ? (
            <Button asChild data-testid="btn-open-dashboard"><Link to="/dashboard">Open dashboard</Link></Button>
          ) : (
            <Button onClick={handleSignIn} data-testid="btn-signin" className="bg-amber-500 text-stone-950 hover:bg-amber-400 inline-flex items-center gap-2">
              <GoogleGlyph/>Continue with Google
            </Button>
          )}
        </div>
      </header>

      <section className="pt-40 pb-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-amber-400 text-sm tracking-widest uppercase mb-6">For working photographers</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-8">
              The studio<br/>in your <span className="italic text-amber-400">pocket.</span>
            </h1>
            <p className="text-lg text-stone-400 mb-10 max-w-lg leading-relaxed">
              Ingest thousands of frames. Cull with AI in minutes. Deliver luxury client galleries.
              Get paid. Photo Bridge is the operating system for the modern independent photographer.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" onClick={handleSignIn} data-testid="btn-hero-start" className="bg-amber-500 text-stone-950 hover:bg-amber-400 h-12 px-6 inline-flex items-center gap-2">
                <GoogleGlyph/>Start 48h free trial — Continue with Google <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" asChild className="h-12 px-6"><Link to="/discover" data-testid="btn-hero-discover">Explore photographers</Link></Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-stone-500">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400"/>No card upfront</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400"/>48 hours free · then $5.99/mo</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400"/>Cancel anytime</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
            <div className="relative rounded-lg overflow-hidden aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1765292783735-9ec7213b1df1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-stone-950/80 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-40 aspect-square rounded-lg overflow-hidden border-4 border-stone-950 hidden md:block">
              <img src="https://images.unsplash.com/photo-1691483059022-e8ad9f2d9d12?crop=entropy&cs=srgb&fm=jpg&q=85&w=400" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-6 -right-6 w-32 aspect-square rounded-lg overflow-hidden border-4 border-stone-950 hidden md:block">
              <img src="https://images.unsplash.com/photo-1781212007793-2d1c8d2a581d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400" alt="" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 30-second cinematic promo — auto-generated slideshow.
          Muted + autoplay so it plays in every mobile browser. `preload=metadata`
          keeps LCP fast. The poster image gives an instant preview while the
          video streams in, and doubles as a graceful fallback on the (rare)
          browsers that can't play H.264 baseline. */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="landing-promo-section">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl mb-10">
            <p className="text-amber-400 text-sm tracking-widest uppercase mb-4">See it in 30 seconds</p>
            <h2 className="font-display text-4xl md:text-5xl mb-6">The whole studio, in one flow.</h2>
            <p className="text-stone-400 leading-relaxed">
              From the first cull to the final delivery — this is what running your business on Photo Bridge feels like.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-xl overflow-hidden border border-white/10 bg-stone-900 shadow-2xl aspect-video"
          >
            <video
              autoPlay muted loop playsInline
              controls
              preload="metadata"
              poster="https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              className="w-full h-full block object-cover"
              data-testid="landing-promo-video"
            >
              <source src="/photo-bridge-promo.mp4" type="video/mp4" />
            </video>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-amber-400 text-sm tracking-widest uppercase mb-4">Every tool. One studio.</p>
            <h2 className="font-display text-4xl md:text-5xl mb-6">Built by photographers, for photographers.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-6 rounded-md bg-stone-900/50 border border-white/5 hover:border-amber-500/30 transition-colors">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 text-amber-400 flex items-center justify-center mb-4"><f.icon className="w-5 h-5"/></div>
                <h3 className="font-medium mb-2">{f.title}</h3>
                <p className="text-sm text-stone-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl mb-6">Ready to run a tighter studio?</h2>
          <p className="text-stone-400 mb-8 max-w-xl mx-auto">Join photographers around the world who deliver faster, sell more prints, and get paid on time.</p>
          <Button size="lg" onClick={handleSignIn} data-testid="btn-footer-cta" className="bg-amber-500 text-stone-950 hover:bg-amber-400 h-12 px-8">
            Start with Google <ArrowRight className="w-4 h-4 ml-2"/>
          </Button>
          <p className="mt-6 text-xs text-stone-500 max-w-md mx-auto" data-testid="signup-consent">
            By continuing, you agree to our <Link to="/legal/terms" className="text-amber-400 hover:underline">Terms of Service</Link>, <Link to="/legal/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link> and <Link to="/legal/acceptable-use" className="text-amber-400 hover:underline">Acceptable Use Policy</Link>.
          </p>
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-white/5 text-sm text-stone-500">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-6 justify-between">
          <div>© {new Date().getFullYear()} Photo Bridge</div>
          <div className="flex gap-6 flex-wrap">
            <Link to="/pricing" className="hover:text-stone-300">Pricing</Link>
            <Link to="/discover" className="hover:text-stone-300">Discover</Link>
            <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-stone-300">Terms</Link>
            <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-stone-300">Privacy</Link>
            <Link to="/legal/cookies" data-testid="footer-cookies" className="hover:text-stone-300">Cookies</Link>
            <Link to="/legal/acceptable-use" data-testid="footer-aup" className="hover:text-stone-300">Acceptable use</Link>
            <Link to="/legal/dmca" data-testid="footer-dmca" className="hover:text-stone-300">DMCA</Link>
            <Link to="/legal/refund" data-testid="footer-refund" className="hover:text-stone-300">Refunds</Link>
          </div>
        </div>
      </footer>
      <ConsentModal open={consentOpen} onOpenChange={setConsentOpen} onAccept={doRedirect} />
    </div>
  );
}
